import { useState } from "react";
import axios from "axios";
import { ArrowRight, Mail, Calendar, Phone } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [status, setStatus] = useState({ state: "idle", msg: "" });

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      setStatus({ state: "error", msg: "Please complete name, email and message." });
      return;
    }
    setStatus({ state: "loading", msg: "" });
    try {
      const res = await axios.post(`${API}/contact`, form);
      const sent = res.data?.email_sent;
      setStatus({
        state: "success",
        msg: sent
          ? "Thanks — your message has landed. I'll be in touch within 24 hours."
          : "Thanks — message received. I'll be in touch within 24 hours."
      });
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      setStatus({
        state: "error",
        msg: err.response?.data?.detail || "Something went wrong. Please try again or email directly."
      });
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="border-t border-neutral-800">
      <div  className={`max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32 `}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
          <div>
            <p className="overline mb-4">Get In Touch</p>
            <h2 className="font-serif-display text-4xl sm:text-5xl tracking-tight mb-8">
              Let&apos;s see if we&apos;re <span className="italic text-[#36C281]">a fit</span>.
            </h2>
            <p className="text-neutral-400 leading-relaxed text-lg max-w-md">
              A no-pressure discovery call. The goal is to understand your objectives and decide whether working together makes sense.
            </p>

            <div className="mt-12 space-y-6">
              <a
                href="mailto:rob@iscoutproperty.com.au"
                data-testid="contact-email-direct"
                className="flex items-center gap-4 text-neutral-300 hover:text-[#36C281] transition-colors"
              >
                <Mail size={18} className="text-[#36C281]" />
                <span>rob@iscoutproperty.com.au</span>
              </a>
              <a
                href="tel:+61449844097"
                data-testid="contact-phone-direct"
                className="flex items-center gap-4 text-neutral-300 hover:text-[#36C281] transition-colors"
              >
                <Phone size={18} className="text-[#36C281]" />
                <span>0449 844 097</span>
              </a>
              <div className="flex items-center gap-4 text-neutral-300">
                <Calendar size={18} className="text-[#36C281]" />
                <span>Typical response within 24 hours</span>
              </div>
            </div>
          </div>

          <form onSubmit={onSubmit} data-testid="contact-form" className="space-y-2 bg-[#151E1A] border border-neutral-800 rounded-xl p-6 md:p-9 shadow-xl shadow-black/20">
            <p className="text-xs tracking-[0.2em] uppercase text-[#36C281]/80 mb-6">Send an enquiry</p>
            <div>
              <label className="text-xs tracking-[0.2em] uppercase text-neutral-500">Name</label>
              <input
                data-testid="contact-input-name"
                name="name"
                value={form.name}
                onChange={onChange}
                placeholder="Your full name"
                className="line-input"
                autoComplete="name"
              />
            </div>
            <div>
              <label className="text-xs tracking-[0.2em] uppercase text-neutral-500">Email</label>
              <input
                data-testid="contact-input-email"
                name="email"
                type="email"
                value={form.email}
                onChange={onChange}
                placeholder="you@example.com"
                className="line-input"
                autoComplete="email"
              />
            </div>
            <div>
              <label className="text-xs tracking-[0.2em] uppercase text-neutral-500">Phone <span className="text-neutral-500">(optional)</span></label>
              <input
                data-testid="contact-input-phone"
                name="phone"
                value={form.phone}
                onChange={onChange}
                placeholder="04XX XXX XXX"
                className="line-input"
                autoComplete="tel"
              />
            </div>
            <div>
              <label className="text-xs tracking-[0.2em] uppercase text-neutral-500">Message</label>
              <textarea
                data-testid="contact-input-message"
                name="message"
                rows={4}
                value={form.message}
                onChange={onChange}
                placeholder="Tell me about your goals, budget and timeline..."
                className="line-input"
              />
            </div>

            <div className="pt-6 flex flex-col sm:flex-row sm:items-center gap-5">
              <button
                type="submit"
                data-testid="contact-submit-btn"
                disabled={status.state === "loading"}
                className="btn-primary"
              >
                {status.state === "loading" ? "Sending..." : "Send Enquiry"} <ArrowRight size={16} />
              </button>
              {status.msg && (
                <p
                  data-testid="contact-status-msg"
                  className={`text-sm ${
                    status.state === "success" ? "text-[#36C281]" : status.state === "error" ? "text-red-400" : "text-neutral-400"
                  }`}
                >
                  {status.msg}
                </p>
              )}
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}
